/* eslint-disable react-hooks/exhaustive-deps */
import React, { type ReactElement, useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { violationsReportActions } from '../../features';
import ReportModal from '../../features/ViolationsReport/components/ReportModal';
import ReportPreview from '../../features/ViolationsReport/components/ReportPreview';
import useExportStatusPolling from '../../features/ViolationsReport/hooks/use-exportStatusPolling';
import { violationsTabActions } from '../../features/ViolationsTab/data-store/violationsTabSlice';
import type { RootState } from '../../shared/types';

import './Report.css';

/**
 * Layout for the Report page:
 * ECMv6/soddetail/violations/report
 *
 */
function Report(): ReactElement {
  const location = useLocation();
  const dispatch = useDispatch();
  const [showReportModal, setShowReportModal] = useState(false);

  useExportStatusPolling();

  const selectedReport = useSelector(
    (state: RootState) => state.aag.report.selectedReport
  );
  const activeFilters = useSelector(
    (state: RootState) => state.aag.violationsTab.controls.activeFilters
  );
  const hasCleared = useRef(false);

  // Restore last-used report type + modal tab into Redux on refresh (Redux resets; sessionStorage persists)
  useEffect(() => {
    if (!selectedReport) {
      try {
        const raw = sessionStorage.getItem('aag.report.session');
        const stored = raw
          ? (JSON.parse(raw) as {
              reportType?: string;
              reportModalTab?: string;
            })
          : null;

        if (stored?.reportType) {
          const { reportType, reportModalTab: storedModalTab } = stored;

          batch(() => {
            dispatch(violationsReportActions.setSelectedReport(reportType));

            if (storedModalTab) {
              dispatch(
                violationsReportActions.setReportModalTab(storedModalTab)
              );
            }
          });
        } else {
          dispatch(violationsReportActions.resetSelectedReport());
        }
      } catch {
        dispatch(violationsReportActions.resetSelectedReport());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    batch(() => {
      dispatch(violationsReportActions.setReportError({ state: false }));

      // Clear only violation age filter when navigating to report page (only once)
      if (!hasCleared.current && activeFilters.length > 0) {
        dispatch(violationsTabActions.setActiveFilters(activeFilters));
        hasCleared.current = true;
      }
    });
  }, [location, dispatch, activeFilters]);

  return (
    <>
      <ReportPreview setShowReportModal={setShowReportModal} />
      <ReportModal
        isOpen={showReportModal}
        setIsOpen={setShowReportModal}
        isViewAllReport
      />
    </>
  );
}

export default Report;
